<template>
  <BaseModal
    card-width="560px"
    no-padding
    no-toolbar
    no-exit
    @close="$emit('close')"
  >
    <template #default>
      <!-- Modal Header -->
      <div class="relative w-full flex items-center gap-2 p-5 border-b border-border-normal">
        <LensCreditIcon class="rounded-md w-5 h-5" />
        <div class="space-y-1">
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted"
          >
            Get Additional Lens Accounts
          </BaseText>
        </div>
        <button
          class="absolute top-3 right-3 close-btn"
          @click="$emit('close')"
        >
          <TagRemoveIcon
            stroke="#5E6678"
            :width="20"
            :height="20"
          />
        </button>
      </div>
      <!-- Modal Content -->
      <div class="w-full flex flex-col px-5 pt-5">
        <div class="flex items-center gap-1.5">
          <!-- <InfoSimpleIcon class="text-text-muted" /> -->
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted"
          >
            <span class="capitalize">{{ getUserSubscription?.internalProductId?.replace('_', ' ') }}</span>, <span class="text-text-normal">{{ lensCounts.included }} lens account{{ lensCounts.included === 1 ? '' : 's' }} included.</span>
          </BaseText>
          <div v-if="lensCounts.additional > 0" class="bg-background-normal rounded-md p-1 text-text-muted text-xs">
            + {{ lensCounts.additional }} free
          </div>
        </div>
        <div class="bg-background-normal h-0.5 w-full my-5" />
        <div class="flex items-center justify-between gap-2">
          <div class="flex items-center gap-1">
            <BaseText
              type="label"
              size="sm"
              class="text-text-muted"
            >
              Additional Lens
            </BaseText>
            <BaseText
              size="sm"
              class="text-text-normal flex-1"
            >
              ${{ seatCostPerInterval }}/{{ getUserSubscription?.interval === 'year' ? 'yr' : 'mo' }} per seat.
            </BaseText>
          </div>
          <div class="border border-border-normal p-1 flex items-center counter-container">
            <button
              class="change-btn"
              :disabled="newLensCount === lensCounts.included"
              @click="newLensCount--"
            >
              <MinusIcon />
            </button>
            <BaseText class="change-number px-3 min-w-[28px] text-center">
              {{ newLensCount }}
            </BaseText>
            <button
              class="change-btn"
              @click="newLensCount++"
            >
              <PlusIcon />
            </button>
          </div>
        </div>
        <div
          class="p-2 rounded-lg w-full mt-2"
          :class="`alert-${changeMessage.severity}`"
        >
          <BaseText
            type="body"
            size="sm"
            class="whitespace-pre-wrap"
            :class="`text-${changeMessage.severity}`"
          >
            {{ changeMessage.message }}
          </BaseText>
        </div>
        <div class="bg-background-normal h-0.5 w-full my-5" />
      </div>
      <div class="px-5">
        <div
          class="w-full flex items-end justify-between"
        >
          <div class="space-y-2">
            <BaseText
              type="label"
              size="sm"
              class="text-text-muted"
            >
              Total
            </BaseText>
            <BaseText
              size="sm"
              class="text-text-normal"
            >
              {{ newLensCountLimit }} Additional Lens Accounts
            </BaseText>
          </div>
          <div class="flex items-center gap-1 text-text-normal">
            <BaseText
              class="text-text-muted font-medium"
            >
              ~${{ approximateProratedCost }}/{{ getUserSubscription?.interval }}
            </BaseText>
          </div>
        </div>
        <div class="flex items-center gap-1.5 text-text-normal mt-3">
          <InfoSimpleIcon />
          <BaseText
            type="label"
            size="xs"
          >
            Estimated prorated cost for the rest of your billing period
          </BaseText>
        </div>
      </div>
      <div
        class="bg-background-normal h-0.5 w-full mt-5"
      />
      <!-- Modal Actions Footer -->
      <div class="w-full flex justify-between p-5">
        <button
          class="px-2 py-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-25"
          @click="$emit('close')"
        >
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted"
          >
            Cancel
          </BaseText>
        </button>
        <button
          class="black-btn-primary py-1.5 px-3 rounded-md"
          :disabled="!isFormValid.bool || loadingSubmit"
          @click="updateTeamSeat"
        >
          <BaseLoadingSpinner
            v-if="loadingSubmit"
            small
          />
          <BaseText
            v-else
            type="label"
            size="sm"
            :class="!isFormValid.bool || loadingSubmit ? 'text-text-disabled' : 'text-white'"
          >
            Confirm
          </BaseText>
        </button>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import ForeplayAPI from '@/api/foreplayServer'
import { mapGetters, mapMutations } from 'vuex'
// Icons
import LensCreditIcon from '../../globals/Icons/LensIcons/LensCreditIcon.vue'
import TagRemoveIcon from '../../globals/Icons/TagRemoveIcon.vue'
import MinusIcon from '../../globals/Icons/MathOperators/MinusIcon.vue'
import PlusIcon from '../../globals/Icons/MathOperators/PlusIcon.vue'
import InfoSimpleIcon from '../../globals/Icons/InfoSimpleIcon.vue'
import { SUBSCRIPTION_PLANS } from '../../../utils/stripe/productMeta'
export default {
  name: 'LensAccountModal',
  components: {
    LensCreditIcon,
    TagRemoveIcon,
    MinusIcon,
    PlusIcon,
    InfoSimpleIcon
  },
  data () {
    return {
      newLensCount: 0,
      loadingSubmit: false,

      today: Math.floor(new Date().getTime() / 1000)
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getTeam', 'getUserSubscription', 'getLensLimit', 'getTotalLensLimit', 'getAdditionalLensLimit', 'getSpyderLimit']),
    ...mapGetters('LensModule', ['getSidebarLenses']),
    newLensCountLimit () {
      return Math.max(this.newLensCount - this.lensCounts.total, 0)
    },
    lensCounts () {
      const currentPlan = SUBSCRIPTION_PLANS.find(plan => plan.key === this.getUserSubscription?.internalProductId)
      const baseLenses = currentPlan?.baseLens || 1

      const totalPaid = this.getUserSubscription?.addOns?.lens?.quantity - baseLenses
      return {
        used: this.getSidebarLenses?.length || 0,
        included: baseLenses,
        additional: this.getAdditionalLensLimit,
        open: this.getLensLimit - this.getSidebarLenses?.length || 0,
        total: Math.max(this.getLensLimit, baseLenses) || 0,
        paid: Math.max(totalPaid, 0)
      }
    },
    seatCostPerInterval () {
      const interval = this.getUserSubscription?.interval || 'month'
      // Monthly: $49.99/month, Annual: $499.99/year
      return interval === 'year' ? 499.99 : 49.99
    },
    approximateProratedCost () {
      const periodStart = this.getUserSubscription?.currentPeriodStart
      const periodEnd = this.getUserSubscription?.currentPeriodEnd
      const totalDuration = periodEnd - periodStart
      const remainingDuration = periodEnd - this.today
      const fullPrice = (this.newLensCount - this.lensCounts.total) * this.seatCostPerInterval
      const fractionRemaining = remainingDuration / totalDuration
      const proration = fullPrice * fractionRemaining

      const totalPrice = Number(proration.toFixed(2))
      return Math.max(totalPrice, 0)
    },
    teamSeatCost () {
      return (this.newLensCount - this.lensCounts.included) * this.seatCostPerInterval
    },
    changeMessage () {
      const totalAvailable = this.newLensCount + this.lensCounts.additional
      if (totalAvailable < this.lensCounts.used) {
        return {
          message: 'Some lens accounts are still used — remove those lenses to free them up.',
          severity: 'error'
        }
      }

      return {
        message: 'Additional lens accounts may be billed separately.',
        severity: 'info'
      }
    },
    isFormValid () {
      // Check for too few seats (can't go below current team size)
      if (this.newLensCount < this.lensCounts.used) {
        return {
          bool: false,
          severity: 'error',
          message: `You have ${this.lensCounts.used} lens account${this.lensCounts.used === 1 ? '' : 's'} assigned. You cannot reduce seats below this number.`
        }
      }
      // Check if value has changed
      if (this.newLensCount !== this.lensCounts.total) {
        return {
          bool: true,
          severity: 'success',
          message: 'You can update your additional lens accounts allocation.'
        }
      }
      // No change
      return {
        bool: false,
        severity: 'info',
        message: 'No changes have been made to lens account allocation.'
      }
    }
  },
  mounted () {
    this.newLensCount = this.getLensLimit
  },
  methods: {
    ...mapMutations('AuthModule', ['SET_USER_SUBSCRIPTION', 'SET_APP_LIMITS']),
    async updateTeamSeat () {
      this.loadingSubmit = true
      try {
        const { data: updatedLensLimit } = await ForeplayAPI.Subscription.updateLensLimit(this.newLensCount)
        this.SET_APP_LIMITS({
          spyder: this.getSpyderLimit,
          lens: updatedLensLimit,
          additionalLens: this.getAdditionalLensLimit
        })
        this.$emit('close')
        this.$showAlert({
          type: 'success',
          message: 'Lens accounts updated successfully.'
        })
      } catch (e) {
        console.error(e)
        const errMessage = e.response?.data?.message || 'An unexpected error occurred. Please try again later.'
        this.$showAlert({
          type: 'error',
          message: errMessage
        })
      } finally {
        this.loadingSubmit = false
      }
    }
  }

}
</script>

<style scoped>
@property --submit-btn-opacity-1 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-color {
  syntax: '<color>';
  initial-value: #0063F4;
  inherits: false;
}

.change-btn {
  padding: 0.375rem;
  border-radius: 0.25rem;
  transition: background-color 100ms;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1;
  color: #5E6678;
}
.change-btn:not(:disabled):hover {
  background-color: #ECEFF3;
}
.change-btn:disabled{
  cursor: not-allowed;
  opacity: 0.25;
}
.change-btn :deep(svg) {
  stroke: #5E6678;
}

.change-number {
  color: rgb(48, 53, 70);
  font-weight: 500;
}

.counter-container {
  border-radius: 0.5rem;
}

.close-btn {
  padding: 0.375rem;
  border-radius: 0.25rem;
  transition: background-color 100ms;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background-color: #ECEFF3;
}

.alert-info{
  background-color: #EEF8FF;
}

.text-info{
  color: #002583;
}

.alert-warning{
  background-color: #FFF6DE;
}

.text-warning{
  color: #66381A;
}

.alert-error{
  background-color: #FFEFF3;
}

.text-error{
  color: #7C001F;
}

</style>
